<script setup lang="ts">
import InfiniteLoading from 'v3-infinite-loading'
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Container, Draggable } from 'vue3-smooth-dnd'

import {
  CAMPAIGN_COLUMN_LAYOUT,
  COLLAPSIBLE_COLUMNS_LIST,
  COLUMN_COLORS,
  type DragCardActionType,
  isCardMovedWithinOneColumn,
  modifyColumnDataOnCardDrag,
  STATUS_TITLE_MAP,
} from './CampaignParticipantsColumns.helpers'

import { useUpdateCampaignParticipant } from '@/api/campaignParticipant.api'
import { useGetCampaignParticipantColumn } from '@/api/campaignParticipants.api'
import IconChevron from '@/assets/icons/chevron.svg?component'
import CampaignParticipantCard from '@/components/CampaignParticipants/CampaignParticipantCard.vue'
import CampaignParticipantDetails from '@/components/CampaignParticipants/CampaignParticipantDetails/CampaignParticipantDetails.vue'
import CampaignParticipantRefreshModal from '@/components/CampaignParticipants/CampaignParticipantRefreshModal.vue'
import CampaignParticipantsMoveToDoneConfirm from '@/components/CampaignParticipants/CampaignParticipantsMoveToDoneConfirm.vue'
import CampaignParticipantsRejectConfirm from '@/components/CampaignParticipants/CampaignParticipantsRejectConfirm.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useShouldDisableMoveToDoneWarningPopup } from '@/lib/hooks/user-preferences.hooks'
import type { CampaignType } from '@/types'
import { type CampaignParticipantType, ParticipantStatus } from '@/types/campaignParticipant.types'
import type { Currency } from '@/types/store'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = defineProps<{
  campaign: CampaignType
  currency: Currency
  searchQuery: string
  shouldUpdateFirstColumn: boolean
}>()
const emits = defineEmits(['onColumnsUpdate'])
const router = useRouter()
const route = useRoute()

const {
  data,
  isLoading,
  columnsLoadingStatus,
  columnsList,
  columnsLayout,
  getColumnData,
  getAllColumnsData,
  updateColumnItems,
  updateColumnItem,
  moveParticipantToColumn,
} = useGetCampaignParticipantColumn(props.campaign)
const { updateParticipantStatus, isLoading: isStatusUpdateLoading } = useUpdateCampaignParticipant(props.campaign.id)
const { shouldDisableMoveToDoneWarningPopup } = useShouldDisableMoveToDoneWarningPopup()
const collapsibleColumnsOpen = reactive<Partial<Record<ParticipantStatus, boolean>>>({})
const isConfirmRejectDialogOpen = ref(false)
const isConfirmDoneDialogOpen = ref(false)
const pendingParticipantRejection = ref<CampaignParticipantType | null>(null)
const pendingParticipantCompletion = ref<CampaignParticipantType | null>(null)

const shouldShowConfirmDialog = (status: ParticipantStatus): status is ParticipantStatus.StoreDenied | ParticipantStatus.Done => {
  const campaignHasFixedFee = Boolean(props.campaign.hasAdditionalPayment)
  return (
    status === ParticipantStatus.StoreDenied ||
    (status === ParticipantStatus.Done && campaignHasFixedFee && !shouldDisableMoveToDoneWarningPopup.value)
  )
}

const onColumnsUpdate = () => {
  emits(
    'onColumnsUpdate',
    columnsList.value.includes(ParticipantStatus.HavePosted) && data.value?.[ParticipantStatus.HavePosted]?.items?.length,
  )
}

const loadColumnPage = async (
  event: { complete: () => void; error: () => void; loaded: () => void; loading: () => void },
  type: ParticipantStatus,
) => {
  const currentPage = data.value?.[type]?.page
  const lastPage = data.value?.[type]?.lastPage

  if (currentPage && lastPage && currentPage < lastPage && type) {
    event.loading()
    await getColumnData({ columnType: type, page: currentPage + 1, searchQuery: props.searchQuery })
    onColumnsUpdate()
    if (currentPage + 1 === lastPage) {
      event.complete()
    } else {
      event.loaded()
    }
  }
}

const dropPlaceholderOptions = {
  className: 'drop-preview',
  animationDuration: '150',
  showOnTop: true,
}

const getCardPayload = (columnId: ParticipantStatus, index: number) => data.value?.[columnId]?.items[index]

const moveCard = (columnId: ParticipantStatus, event: DragCardActionType) => {
  const cards = data.value?.[columnId]?.items
  // Every time we move a card from column to column, we need to update the status as we can't reload the data from server
  const updatedEvent = event.payload ? { ...event, ...{ payload: { ...event.payload, status: columnId } } } : event
  const updatedItems = modifyColumnDataOnCardDrag(cards, updatedEvent, data.value?.[columnId]?.total)
  updateColumnItems({ columnStatus: columnId, items: updatedItems.items, total: updatedItems.total }) // TODO: use this instead of store.dispatch
}

const onCardDrop = async (columnId: ParticipantStatus, event: DragCardActionType) => {
  const callbackToExecuteBeforeMoving: Record<ParticipantStatus.StoreDenied | ParticipantStatus.Done, () => void> = {
    [ParticipantStatus.StoreDenied]: () => {
      if (event.payload) {
        pendingParticipantRejection.value = event.payload
      }
      isConfirmRejectDialogOpen.value = true
    },
    [ParticipantStatus.Done]: () => {
      if (event.payload) {
        pendingParticipantCompletion.value = event.payload
      }
      isConfirmDoneDialogOpen.value = true
    },
  }
  if (event.removedIndex !== null || event.addedIndex !== null) {
    moveCard(columnId, event)
    if (
      event.addedIndex !== null &&
      !isCardMovedWithinOneColumn(event.addedIndex, event.removedIndex) &&
      shouldShowConfirmDialog(columnId)
    ) {
      callbackToExecuteBeforeMoving[columnId]()
    } else {
      if (event.addedIndex !== null && event.payload?.status !== columnId && event.payload?.id) {
        await updateParticipantStatus(event.payload?.id, columnId)
      }
    }
  }
}

const onCancelStrategy: Record<ParticipantStatus.StoreDenied | ParticipantStatus.Done, () => void> = {
  [ParticipantStatus.StoreDenied]: () => {
    if (pendingParticipantRejection.value) {
      moveParticipantToColumn(pendingParticipantRejection.value.status, pendingParticipantRejection, ParticipantStatus.StoreDenied)
      isConfirmRejectDialogOpen.value = false
      pendingParticipantRejection.value = null
    }
  },
  [ParticipantStatus.Done]: () => {
    if (pendingParticipantCompletion.value) {
      moveParticipantToColumn(pendingParticipantCompletion.value.status, pendingParticipantCompletion, ParticipantStatus.Done)
      isConfirmDoneDialogOpen.value = false
      pendingParticipantCompletion.value = null
    }
  },
}

const onConfirmStrategy: Record<ParticipantStatus.StoreDenied | ParticipantStatus.Done, () => Promise<void>> = {
  [ParticipantStatus.StoreDenied]: async () => {
    if (pendingParticipantRejection.value) {
      updateColumnItem({ ...pendingParticipantRejection.value, status: ParticipantStatus.StoreDenied }, 'addComment')
      await updateParticipantStatus(pendingParticipantRejection.value.id, ParticipantStatus.StoreDenied)
      isConfirmRejectDialogOpen.value = false
      pendingParticipantRejection.value = null
    }
  },
  [ParticipantStatus.Done]: async () => {
    if (pendingParticipantCompletion.value) {
      await updateParticipantStatus(pendingParticipantCompletion.value.id, ParticipantStatus.Done)
      isConfirmDoneDialogOpen.value = false
      pendingParticipantCompletion.value = null
    }
  },
}

watch(
  () => data.value,
  () => onColumnsUpdate(),
  { deep: true },
)

watch(
  () => props.searchQuery,
  async () => await getAllColumnsData(props.searchQuery),
)

watch(
  () => props.shouldUpdateFirstColumn,
  async () => {
    if (props.shouldUpdateFirstColumn) {
      await getColumnData({ columnType: columnsList.value[0], page: 1, searchQuery: props.searchQuery })
    }
  },
  { immediate: true },
)

const isMoveToDoneDialogVisible = computed(() => !shouldDisableMoveToDoneWarningPopup.value && isConfirmDoneDialogOpen.value)
</script>
<template>
  <LoadingSpinner v-if="!data || !columnsLayout || isLoading" />
  <div v-else class="columns-wrapper">
    <FlexComponent :gap="16">
      <div v-for="(column, index) in CAMPAIGN_COLUMN_LAYOUT[columnsLayout]" :key="index" class="column-wrapper">
        <div v-for="statusId in column" :key="statusId" class="column-wrapper__i" :style="{ backgroundColor: COLUMN_COLORS[statusId] }">
          <template v-if="data[statusId]">
            <BoxComponent :mb="16" :pl="16" :pr="16">
              <FlexComponent justify="space-between">
                <TypographyComponent variant="title-large" color="#000000">
                  {{ STATUS_TITLE_MAP[statusId] }}
                </TypographyComponent>
                <div v-if="data[statusId]?.total" class="column-wrapper__count">
                  {{ data[statusId]?.total }}
                </div>
              </FlexComponent>
            </BoxComponent>
            <div
              :class="{
                column__scrollable: true,
                [columnsLayout]: true,
                [statusId]: true,
                collapsible: COLLAPSIBLE_COLUMNS_LIST.includes(statusId),
                full: collapsibleColumnsOpen[statusId] && data?.[statusId]?.items?.length > 1,
              }"
            >
              <Container
                class="column"
                :drop-placeholder="dropPlaceholderOptions"
                :get-child-payload="(i: number) => getCardPayload(statusId, i)"
                group-name="participants"
                drag-class="column__drag-card"
                drop-class="column__ghost-drop"
                @drop="(event: DragCardActionType) => onCardDrop(statusId, event)"
              >
                <Draggable v-for="card in data[statusId]?.items" :key="card.id">
                  <!-- @vue-expect-error todo fix me-->
                  <CampaignParticipantCard
                    :item="card"
                    :has-fixed-fee="campaign.hasAdditionalPayment"
                    :currency="currency"
                    @click="
                      () => {
                        router.push({ query: { participant: card.id } })
                      }
                    "
                  />
                </Draggable>
                <BoxComponent v-if="columnsLoadingStatus[statusId]" :mt="24">
                  <FlexComponent justify="space-around">
                    <el-icon :size="42" color="black">
                      <LoadingSpinner :size="42" />
                    </el-icon>
                  </FlexComponent>
                </BoxComponent>
                <InfiniteLoading :identifier="statusId" @infinite="loadColumnPage($event, statusId)">
                  <template #complete><div /></template>
                  <template #spinner />
                </InfiniteLoading>
              </Container>
            </div>
            <FlexComponent
              v-if="COLLAPSIBLE_COLUMNS_LIST.includes(statusId) && data[statusId]?.items?.length > 1"
              :gap="8"
              align="center"
              justify="center"
              class="column__view-more"
              @click="() => (collapsibleColumnsOpen[statusId] = !Boolean(collapsibleColumnsOpen[statusId]))"
            >
              <TypographyComponent variant="title-medium" color="#000000">{{
                collapsibleColumnsOpen[statusId] ? 'View less' : 'View more'
              }}</TypographyComponent>
              <el-icon :size="18" :class="{ 'column__view-more__icon': true, up: collapsibleColumnsOpen[statusId] }">
                <IconChevron />
              </el-icon>
            </FlexComponent>
          </template>
        </div>
      </div>
      <!-- @vue-expect-error todo fix me-->
      <CampaignParticipantsRejectConfirm
        v-model="isConfirmRejectDialogOpen"
        :participant="pendingParticipantRejection"
        :campaign-id="campaign.id"
        @on-cancel="onCancelStrategy[ParticipantStatus.StoreDenied]"
        @on-confirm="onConfirmStrategy[ParticipantStatus.StoreDenied]"
      />
      <CampaignParticipantsMoveToDoneConfirm
        v-model="isMoveToDoneDialogVisible"
        :on-submit="(value: boolean) => (shouldDisableMoveToDoneWarningPopup = value)"
        :is-loading="isStatusUpdateLoading"
        @on-cancel="onCancelStrategy[ParticipantStatus.Done]"
        @on-confirm="onConfirmStrategy[ParticipantStatus.Done]"
      />
    </FlexComponent>
  </div>
  <!-- @vue-expect-error todo fix me-->
  <CampaignParticipantDetails
    v-if="!!route.query.participant"
    :has-fixed-fee="campaign.hasAdditionalPayment"
    :is-open="!!route.query.participant"
    :campaign="campaign"
    :columns-list="columnsList"
    :columns-data="data"
    @move-participant-to-column="moveParticipantToColumn"
    @update-column-item="updateColumnItem"
  />
  <CampaignParticipantRefreshModal @refresh="getAllColumnsData(props.searchQuery)" />
</template>

<style scoped lang="scss">
.columns-wrapper {
  width: 100%;
  padding-bottom: 24px;
  overflow-x: auto;
  &::-webkit-scrollbar {
    height: 8px;
    background: rgba(3, 30, 81, 0.12);
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(3, 30, 81, 0.3);
    border-radius: 8px;
  }
}
.column-wrapper {
  flex: 0 0 290px;
  overflow: hidden;
}

.column-wrapper__i {
  margin-bottom: 16px;
  padding: 16px 8px 16px 16px;
  border-radius: 16px;
}

.column__scrollable {
  max-height: calc(100vh - 254px);
  min-height: 100px;
  padding-right: 8px;
  overflow-y: auto;
  border-radius: 16px;

  &::-webkit-scrollbar {
    width: 8px;
    background: rgba(3, 30, 81, 0.12);
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(3, 30, 81, 0.3);
    border-radius: 8px;
  }

  &.collapsible {
    max-height: 100px;
    overflow: hidden;
  }

  &.collapsible.full {
    max-height: calc(100vh - 454px);
    min-height: 172px;
    overflow-y: auto;
  }

  // Columns height differ based on the campaign type and layout
  &.suggestion,
  &.second_prio {
    max-height: calc(50vh - 127px);
  }

  &.proposal.open,
  &.proposal.accepted,
  &.proposalWithSeeding.open,
  &.proposalWithSeeding.accepted,
  &.creatorApproval.accepted,
  &.creatorApprovalWithSeeding.accepted,
  &.advertiserApproval.accepted,
  &.advertiserApprovalWithSeeding.accepted,
  &.advertiserApproval.applied,
  &.advertiserApprovalWithSeeding.applied {
    max-height: calc(100vh - 454px);
  }
}

.column__view-more {
  padding-top: 16px;
  cursor: pointer;
}

.column__view-more__icon {
  transition: transform 0.33s;
  transition-delay: 0.1s;
  transform: rotate(180deg);

  &.up {
    transform: rotate(0deg);
  }
}

.column-wrapper__count {
  height: 24px;
  padding: 0 8px;
  border-radius: 24px;

  font-size: 11px;
  line-height: 24px;
  font-weight: 500;
  color: #000000;
  background: rgba(255, 255, 255, 0.48);
}

.smooth-dnd-container {
  min-height: 100px;
}

.smooth-dnd-ghost {
  transition: transform 0.3s ease-in-out;
}

:deep(.column) {
  .drop-preview {
    margin-bottom: 8px;
    border-radius: 16px;
    background: rgba(0, 0, 0, 0.1);
  }

  .column__ghost-drop {
    transform: scale(1.04);
  }
}

.column__drag-card {
  transform: rotateZ(6deg);
  box-shadow:
    0 2px 2px 0 rgba(0, 0, 0, 0.1),
    0 3px 4px 2px rgba(0, 0, 0, 0.1);
}
</style>
